interface Dimensions {
  width: number;
  height: number;
}

const NAMED_RATIOS: [string, number][] = [
  ["1:1", 1],
  ["4:5", 4 / 5],
  ["9:16", 9 / 16],
  ["16:9", 16 / 9],
  ["1.91:1", 1.91],
  ["3:2", 3 / 2],
  ["2:3", 2 / 3],
  ["4:3", 4 / 3],
  ["3:4", 3 / 4],
];

// Ratios Meta accepts per placement: stories are full-screen 9:16, feeds take 4:5 up to 1.91:1.
const PLACEMENT_RATIOS: Record<string, { min: number; max: number; expected: string }> = {
  story: { min: 0.5, max: 0.625, expected: "9:16" },
  feed: { min: 0.78, max: 1.95, expected: "between 4:5 and 1.91:1" },
};

export const formatFileSize = (bytes: number | null | undefined) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const aspectRatioLabel = ({ width, height }: Dimensions) => {
  const ratio = width / height;
  const named = NAMED_RATIOS.find(([, value]) => Math.abs(ratio - value) / value < 0.02);
  return named ? named[0] : `${ratio.toFixed(2)}:1`;
};

export const placementWarning = (dimensions: Dimensions | null, placements: string[]) => {
  if (!dimensions) return null;
  const ratio = dimensions.width / dimensions.height;
  const mismatches = placements.filter((placement) => {
    const rule = PLACEMENT_RATIOS[placement];
    return rule && (ratio < rule.min || ratio > rule.max);
  });
  if (!mismatches.length) return null;

  const expectations = mismatches.map((placement) => `${placement} expects ${PLACEMENT_RATIOS[placement].expected}`);
  return `${aspectRatioLabel(dimensions)} image: ${expectations.join(", ")}`;
};

export const mediaFormatLabel = (contentType: string | null | undefined, url: string) => {
  const fromType = contentType?.split("/")[1];
  const fileName = url.split(/[?#]/)[0].split("/").pop() ?? "";
  const fromUrl = fileName.match(/\.([a-z0-9]{2,5})$/i)?.[1];
  return (fromType || fromUrl || "").toUpperCase();
};

interface SelectableAsset {
  id: number;
  name: string;
  asset_tags?: string[];
}

// Same text as the claude.ai media gallery sends, so a pasted selection reads the same to Claude.
export const selectionForClaude = (assets: SelectableAsset[]) => {
  const lines = assets.map(
    (asset) => `- ${asset.id} · ${asset.name}${asset.asset_tags?.length ? ` [${asset.asset_tags.join(", ")}]` : ""}`,
  );
  return `I picked these library images (ids):\n${lines.join("\n")}`;
};
